/**
 * Ações de mensagem (responder citando, reagir, encaminhar, editar):
 * capacidades por canal e normalização do que a bolha precisa saber.
 *
 * COMPARTILHADO pelos 3 shells (wa-personal, wa-business e components/inbox).
 * Fonte única das regras: utils/channels.js (espelho do backend
 * services/channel_capabilities.py). Nunca compare o código do canal na
 * tela: pergunte aqui.
 *
 * Pedido da Gelo Americano (07 e 08/10/2026): "como respondo em cima de uma
 * mensagem?", "como edito?", "não consigo reagir", "encaminhar fica escondido".
 */
import { supports, whyDisabled } from '../../utils/channels';

/** Janela em que o WhatsApp aceita editar a própria mensagem. */
export const EDIT_WINDOW_MS = 15 * 60 * 1000;
/** Janela do WhatsApp para apagar para todos (~2 dias). */
export const DELETE_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

/** Reações rápidas (as 6 do WhatsApp) e a grade completa do "mais". */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
export const MORE_REACTIONS = [
  '👍', '👎', '❤️', '🔥', '🎉', '👏', '😂', '😍', '😮', '😢', '😡', '🙏',
  '✅', '❌', '⭐', '💯', '🤝', '👀', '💪', '🤔', '😅', '🥳', '😎', '🙌',
  '📌', '📅', '💰', '🚚', '📦', '🧊', '☕', '🍻', '🙂', '😉', '🤗', '😴',
];

/**
 * O que o canal permite, já com o motivo quando não permite (regra da
 * skill paridade-4-inboxes: item de tela compartilhada fica DESABILITADO com
 * o motivo à vista, nunca some).
 */
export function messageActionCaps(channel) {
  return {
    quote: supports(channel, 'quote', 'native'),
    whyQuote: whyDisabled(channel, 'quote'),
    reactions: supports(channel, 'reactions', 'native'),
    whyReactions: whyDisabled(channel, 'reactions'),
    edit: supports(channel, 'edit', 'native'),
    whyEdit: whyDisabled(channel, 'edit'),
    forward: supports(channel, 'forward'),
    // DIV-19 (2026-10-20): apagar para todos (so o QR, pela Evolution).
    deleteForEveryone: supports(channel, 'delete_for_everyone', 'native'),
    whyDelete: whyDisabled(channel, 'delete_for_everyone'),
  };
}

/**
 * A mensagem ainda pode ser editada? Só a própria, de texto, dentro da
 * janela de 15 min e num canal com edição. Devolve '' quando pode, senão o
 * motivo curto para o item desabilitado.
 */
export function whyCannotEdit({ fromMe, hasText, hasMedia, timestampMs, deleted }, caps) {
  if (!caps.edit) return caps.whyEdit || 'Este canal não edita mensagem enviada';
  if (deleted) return 'Mensagem apagada';
  if (!fromMe) return 'Só dá para editar mensagem enviada por você';
  if (!hasText || hasMedia) return 'Só mensagem de texto pode ser editada';
  if (timestampMs && Date.now() - timestampMs > EDIT_WINDOW_MS) {
    return 'O WhatsApp só aceita edição até 15 minutos depois do envio';
  }
  return '';
}

/**
 * A mensagem ainda pode ser apagada para todos? So a propria, num canal com
 * o recurso, dentro de ~2 dias (DIV-19). '' quando pode, senao o motivo.
 */
export function whyCannotDelete({ fromMe, timestampMs, deleted }, caps) {
  if (!caps.deleteForEveryone) return caps.whyDelete || 'Este canal não apaga mensagem enviada';
  if (deleted) return 'Mensagem já apagada';
  if (!fromMe) return 'Só dá para apagar mensagem enviada por você';
  if (timestampMs && Date.now() - timestampMs > DELETE_WINDOW_MS) {
    return 'O WhatsApp só apaga para todos até 2 dias depois do envio';
  }
  return '';
}

/** Texto curto da citação quando a mensagem citada é mídia sem legenda. */
export const QUOTED_MEDIA_LABEL = {
  image: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
  document: 'Documento',
  sticker: 'Figurinha',
};

/**
 * Monta o bloco `quoted` (mesmo contrato do backend) a partir da mensagem
 * que o operador escolheu responder, para a prévia acima do composer e a
 * bolha otimista. `msg` normalizada pelo shell: { id, externalId, text,
 * fromMe, mediaType, senderName }.
 */
export function quotedFromMessage(msg) {
  if (!msg) return null;
  return {
    id: msg.id || null,
    external_id: msg.externalId || null,
    text: (msg.text || '').trim() || QUOTED_MEDIA_LABEL[msg.mediaType] || null,
    from_me: !!msg.fromMe,
    media_type: msg.mediaType || null,
    sender_name: msg.senderName || null,
  };
}
